import cn from 'classnames';
import { Children, ReactElement, ReactNode } from 'react';

import './Table.module.css';

interface ColumnProps {
  /**
   * Alignment of the column
   */
  align?: 'left' | 'center' | 'right';
  /**
   * If you want to have smaller column width
   */
  fitContent?: boolean;
  /**
   * Title of this column
   */
  title?: string;
  /**
   * Display field of the data record
   */
  dataIndex: string | string[];
  /**
   * Unique key of this column, you can ignore this prop if you've set a unique dataIndex
   */
  key?: string;
  /**
   * Renderer of the table cell. The return value should be a ReactNode
   */
  render?: (value: any, record: any, index: number) => ReactNode;
}

function Column(props: ColumnProps) {
  return null;
}

function getData(data: any, dataIndex: string | string[]) {
  if (Array.isArray(dataIndex)) {
    return dataIndex.reduce((acc, key) => acc[key], data);
  } else {
    return data[dataIndex];
  }
}

export interface TableProps {
  // FIXME: Use correct type
  children: any;
  className?: string;
  dataKey?: string;
  dataSource?: any[];
}

export function Table({
  dataKey = 'key',
  className,
  dataSource,
  children,
}: TableProps) {
  return (
    <div className={cn('flex flex-col', className)}>
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {Children.map(
                    children,
                    (child: ReactElement<ColumnProps>) => (
                      <th
                        key={
                          child.key ||
                          (Array.isArray(child.props.dataIndex)
                            ? child.props.dataIndex.join('/')
                            : child.props.dataIndex)
                        }
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {child.props.title}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dataSource?.map((data: any, index: number) => (
                  <tr key={data[dataKey]}>
                    {Children.map(
                      children,
                      (child: ReactElement<ColumnProps>) => (
                        <td
                          className={cn(
                            'px-6 py-4 whitespace-nowrap text-sm text-gray-500',
                            {
                              'w-px': child.props.fitContent === true,
                              'text-left': child.props.align === 'left',
                              'text-center': child.props.align === 'center',
                              'text-right': child.props.align === 'right',
                            },
                          )}
                          key={`${child.key || child.props.dataIndex}-${index}`}
                        >
                          {child.props.render
                            ? child.props.render(
                                getData(data, child.props.dataIndex),
                                data,
                                index,
                              )
                            : getData(data, child.props.dataIndex)}
                        </td>
                      ),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

Table.Column = Column;

Table.displayName = 'Table';

export default Table;
